import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

// Keeping the interface for documentation purposes
// interface AppState {
//   isLoading: boolean;
//   loadingMessage: string;
//   sidebarOpen: boolean;
//   theme: 'light' | 'dark' | 'system';
//   locale: string;
//   notifications: Notification[];
//   modal: ModalState;
//   confirmation: ConfirmationState;
// }

interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  duration?: number;
  persistent?: boolean;
  actions?: NotificationAction[];
}

interface NotificationAction {
  label: string;
  action: () => void;
  style?: 'primary' | 'secondary' | 'danger';
}

interface ModalState {
  isOpen: boolean;
  component: string | null;
  props: Record<string, any>;
  options: ModalOptions;
}

interface ModalOptions {
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  closable?: boolean;
  persistent?: boolean;
  backdrop?: boolean;
}

interface ConfirmationState {
  isOpen: boolean;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'danger';
  confirmText: string;
  cancelText: string;
  onConfirm: (() => void) | null;
  onCancel: (() => void) | null;
}

export const useAppStore = defineStore('app', () => {
  // State
  const isLoading = ref(false);
  const loadingMessage = ref('Carregando...');
  const sidebarOpen = ref(true);
  const theme = ref<'light' | 'dark' | 'system'>('system');
  const locale = ref('pt-BR');
  const notifications = ref<Notification[]>([]);
  const modal = ref<ModalState>({
    isOpen: false,
    component: null,
    props: {},
    options: {},
  });
  const confirmation = ref<ConfirmationState>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    confirmText: 'Confirmar',
    cancelText: 'Cancelar',
    onConfirm: null,
    onCancel: null,
  });

  // Getters
  const isDark = computed(() => {
    if (theme.value === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return theme.value === 'dark';
  });

  const unreadNotificationsCount = computed(() => {
    return notifications.value.length;
  });

  // Actions
  const setLoading = (loading: boolean, message = 'Carregando...') => {
    isLoading.value = loading;
    loadingMessage.value = message;
  };

  const toggleSidebar = () => {
    sidebarOpen.value = !sidebarOpen.value;
    localStorage.setItem('sidebarOpen', sidebarOpen.value.toString());
  };

  const setSidebarOpen = (open: boolean) => {
    sidebarOpen.value = open;
    localStorage.setItem('sidebarOpen', open.toString());
  };

  const setTheme = (newTheme: 'light' | 'dark' | 'system') => {
    theme.value = newTheme;
    localStorage.setItem('theme', newTheme);
    applyTheme();
  };

  const applyTheme = () => {
    const html = document.documentElement;
    
    if (theme.value === 'dark' || (theme.value === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
      html.classList.add('dark');
    } else {
      html.classList.remove('dark');
    }
  };

  
  
  const setLocale = (newLocale: string) => {
    locale.value = newLocale;
    localStorage.setItem('locale', newLocale);
  };

  const addNotification = (notification: Omit<Notification, 'id'>) => {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    const newNotification: Notification = {
      id,
      duration: 5000,
      persistent: false,
      ...notification,
    };

    notifications.value.push(newNotification);

    // Auto remove notification if not persistent
    if (!newNotification.persistent && newNotification.duration) {
      setTimeout(() => {
        removeNotification(id);
      }, newNotification.duration);
    }

    return id;
  };

  const removeNotification = (id: string) => {
    const index = notifications.value.findIndex(n => n.id === id);
    if (index > -1) {
      notifications.value.splice(index, 1);
    }
  };

  const clearNotifications = () => {
    notifications.value = [];
  };

  const showSuccess = (title: string, message?: string, options?: Partial<Notification>) => {
    return addNotification({
      type: 'success',
      title,
      message: message || '',
      ...options,
    });
  };

  const showError = (title: string, message?: string, options?: Partial<Notification>) => {
    return addNotification({
      type: 'error',
      title,
      message: message || '',
      persistent: true,
      ...options,
    });
  };

  const showWarning = (title: string, message?: string, options?: Partial<Notification>) => {
    return addNotification({
      type: 'warning',
      title,
      message: message || '',
      ...options,
    });
  };

  const showInfo = (title: string, message?: string, options?: Partial<Notification>) => {
    return addNotification({
      type: 'info',
      title,
      message: message || '',
      ...options,
    });
  };

  const openModal = (
    component: string,
    props: Record<string, any> = {},
    options: ModalOptions = {}
  ) => {
    modal.value = {
      isOpen: true,
      component,
      props,
      options: {
        size: 'md',
        closable: true,
        persistent: false,
        backdrop: true,
        ...options,
      },
    };
  };

  const closeModal = () => {
    modal.value = {
      isOpen: false,
      component: null,
      props: {},
      options: {},
    };
  };

  const showConfirmation = (
    title: string,
    message: string,
    options: Partial<ConfirmationState> = {}
  ): Promise<boolean> => {
    return new Promise((resolve) => {
      confirmation.value = {
        isOpen: true,
        title,
        message,
        type: 'info',
        confirmText: 'Confirmar',
        cancelText: 'Cancelar',
        onConfirm: () => {
          confirmation.value.isOpen = false;
          resolve(true);
        },
        onCancel: () => {
          confirmation.value.isOpen = false;
          resolve(false);
        },
        ...options,
      };
    });
  };

  const closeConfirmation = () => {
    confirmation.value.isOpen = false;
    if (confirmation.value.onCancel) {
      confirmation.value.onCancel();
    }
  };

  // Initialize store
  const initialize = () => {
    // Load saved preferences
    const savedSidebarOpen = localStorage.getItem('sidebarOpen');
    if (savedSidebarOpen !== null) {
      sidebarOpen.value = savedSidebarOpen === 'true';
    }

    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | 'system' | null;
    if (savedTheme) {
      theme.value = savedTheme;
    }

    const savedLocale = localStorage.getItem('locale');
    if (savedLocale) {
      locale.value = savedLocale;
    }

    // Apply theme
    applyTheme();

    // Listen for system theme changes
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      if (theme.value === 'system') {
        applyTheme();
      }
    });
  };

  // Utility functions
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatCurrency = (value: number, currency = 'BRL'): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency,
    }).format(value);
  };

  const formatDate = (date: string | Date, format = 'short'): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (format === 'short') {
      return dateObj.toLocaleDateString('pt-BR');
    } else if (format === 'long') {
      return dateObj.toLocaleDateString('pt-BR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } else if (format === 'datetime') {
      return dateObj.toLocaleString('pt-BR');
    }
    
    return dateObj.toLocaleDateString('pt-BR');
  };

  const formatTime = (date: string | Date): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const truncateText = (text: string, length = 50): string => {
    if (text.length <= length) return text;
    return text.substring(0, length) + '...';
  };

  const capitalizeFirst = (text: string): string => {
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  };

  const generateInitials = (name: string): string => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  return {
    // State
    isLoading,
    loadingMessage,
    sidebarOpen,
    theme,
    locale,
    notifications,
    modal,
    confirmation,
    
    // Getters
    isDark,
    unreadNotificationsCount,
    
    // Actions
    setLoading,
    toggleSidebar,
    setSidebarOpen,
    setTheme,
    applyTheme,
    setLocale,
    addNotification,
    removeNotification,
    clearNotifications,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    openModal,
    closeModal,
    showConfirmation,
    closeConfirmation,
    initialize,
    
    // Utilities
    formatFileSize,
    formatCurrency,
    formatDate,
    formatTime,
    truncateText,
    capitalizeFirst,
    generateInitials,
  };
});